import React, {useState, useEffect, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    Card,
    Table,
    Button,
    Select,
    Space,
    Tag,
    Tooltip,
    message,
    Modal,
    Input
} from 'antd';
import {SearchOutlined, ExportOutlined, EyeOutlined, WarningOutlined} from '@ant-design/icons';
import {formatCentsAmount} from '../../../utils/invoiceAmount';
import {useQuery, useMutation, useLazyQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {GET_DELIVERY_DSP_BILL_LISTING, GET_BILL_STATUS_FILTER_OPTIONS} from '../../../graphql/queries/settlement/getSettlementBillQueries';
import {CONFIRM_BILL, EXPORT_BILL, REJECT_BILL_EXTERNAL} from '../../../graphql/mutations/settlement/settlementBillMutations';
import {GET_EXPORT_HISTORY} from '../../../graphql/queries/settlement/billExportQueries';
import {BillTimeRangePicker} from '../../../components/settlement/BillTimeRangePicker';
import {BillExportModal, type ExportLanguage} from '../../../components/settlement/BillExportModal';
import BillExportProgressModal from '../../../components/settlement/BillExportProgressModal';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {renderBillTypeTag, getBillTypeLabel, getBillStatusColor} from '../../../utils/billI18n';
import {formatDateWithTimezone} from '../../../utils/timezoneFormatter';
import {canExportBill, getExportButtonTitle} from '../../../utils/billExportPermission';
import {getUserInfo} from '../../../utils/auth';
import type {
    SettlementBillType,
    BillFiltersInput,
    PageRequestTypeInput,
    BillExportTemplateType
} from '../../../graphql/types/settlement';
import {BILL_LIST_DEFAULT_PAGE_REQUEST, FrontendBillStatusEnum} from '../../../graphql/types/settlement/CommonTypes';
import {
    consumeSettlementBillListState,
    persistBeforeBillDetailNavigation
} from '../../central/settlement/settlementBillListState';

const {Option} = Select;

const DSP_DEFAULT_FILTERS: BillFiltersInput = {
    includeCanceled: true,
    includeDeleted: true,
    billType: 'DSP_DELIVERY_FEE_BILL'
};

function getDeliveryDspListInitialState() {
    const saved = consumeSettlementBillListState('delivery-dsp');
    if (saved) {
        return saved;
    }
    return {
        filters: {...DSP_DEFAULT_FILTERS},
        tempFilters: {...DSP_DEFAULT_FILTERS},
        pageRequest: {...BILL_LIST_DEFAULT_PAGE_REQUEST},
        billIdInput: ''
    };
}

/**
 * Delivery DSP 账单列表（DSP_MANAGER）
 * 使用 getDeliveryDspBillListing：强制 DSP 运费账单且仅本账号绑定 DSP，前端无需传 DSP。
 */
const DeliveryDspBillListPage: React.FC = () => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const [initialState] = useState(getDeliveryDspListInitialState);
    const [filters, setFilters] = useState<BillFiltersInput>(initialState.filters);
    const [tempFilters, setTempFilters] = useState<BillFiltersInput>(initialState.tempFilters);
    const [pageRequest, setPageRequest] = useState<PageRequestTypeInput>(initialState.pageRequest);
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [exportTaskId, setExportTaskId] = useState<string | null>(null);
    const [selectedBill, setSelectedBill] = useState<SettlementBillType | null>(null);
    const roleName = getUserInfo()?.account?.role?.name;
    const isDspOwnerRole = (roleName as string) === 'DSP_OWNER_TEAM' || (roleName as string) === 'DSP_OWNER';

    const {data, loading, refetch} = useQuery(GET_DELIVERY_DSP_BILL_LISTING, {
        variables: {filters, pageRequest},
        fetchPolicy: 'cache-and-network'
    });
    const {data: billStatusOptionsData, loading: billStatusOptionsLoading} = useQuery(GET_BILL_STATUS_FILTER_OPTIONS);

    useEffect(() => {
        const handleBillDataUpdated = () => {
            refetch();
        };
        window.addEventListener('billDataUpdated', handleBillDataUpdated);
        return () => {
            window.removeEventListener('billDataUpdated', handleBillDataUpdated);
        };
    }, [refetch]);

    const [exportBill] = useMutation(EXPORT_BILL);
    const [confirmBill, {loading: confirmBillLoading}] = useMutation(CONFIRM_BILL);
    const [rejectBillExternal] = useMutation(REJECT_BILL_EXTERNAL);
    const [checkExportHistory] = useLazyQuery(GET_EXPORT_HISTORY, {
        fetchPolicy: 'network-only'
    });

    const handleBillTypeChange = (value: string) => {
        setTempFilters(prev => ({...prev, billType: value as any}));
    };

    const handleBillStatusChange = (value: string) => {
        setTempFilters(prev => ({...prev, billStatus: value as any}));
    };

    const handleInvoiceSubmittedChange = (value: boolean | undefined) => {
        setTempFilters(prev => ({...prev, invoiceSubmitted: value}));
    };

    const handleTimeRangeChange = (timeRange: any) => {
        setTempFilters(prev => ({...prev, billTimeRange: timeRange}));
    };

    const handlePageChange = (page: number, size: number) => {
        setPageRequest((prev) => ({...prev, page, size}));
    };

    const handleSearch = useCallback(() => {
        const newFilters: BillFiltersInput = {...tempFilters, billId: undefined};
        if (!newFilters.billType) {
            newFilters.billType = 'DSP_DELIVERY_FEE_BILL';
        }
        setFilters(newFilters);
        setPageRequest((prev) => ({...prev, page: 1, size: 20}));
    }, [tempFilters]);

    const handleViewDetail = (bill: SettlementBillType) => {
        persistBeforeBillDetailNavigation('delivery-dsp', {
            filters,
            tempFilters,
            pageRequest,
            billIdInput: ''
        });
        navigate(`/delivery/dsp-bill-detail/${bill.id}`);
    };

    const handleExportBill = (bill: SettlementBillType) => {
        setSelectedBill(bill);
        setExportModalVisible(true);
    };

    const handleExport = async (template: BillExportTemplateType, language: ExportLanguage) => {
        if (!selectedBill) return;
        try {
            message.loading({content: t('dspBill.exportModal.checkingHistory'), key: 'check', duration: 0});
            const {data: historyData} = await checkExportHistory({
                variables: {
                    billId: selectedBill.id,
                    pageRequest: {page: 1, pageSize: 5}
                }
            });
            message.destroy('check');

            const historyRecords = historyData?.getExportHistory?.content || [];
            const successRecords = historyRecords.filter((r: any) => r.status === 'SUCCESS');

            if (successRecords.length > 0) {
                const latestRecord = successRecords[0];
                const confirmResult = await new Promise<boolean>((resolve) => {
                    Modal.confirm({
                        title: t('dspBill.exportModal.confirmTitle'),
                        content: (
                            <div>
                                <p>{t('dspBill.exportModal.hasHistoryTip')}</p>
                                <ul style={{marginTop: 8, marginLeft: 20}}>
                                    <li>{t('dspBill.exportModal.exportTime')}: {new Date(latestRecord.createdAt).toLocaleString(i18n.language)}</li>
                                    <li>{t('dspBill.exportModal.fileName')}: {latestRecord.fileName}</li>
                                    <li>{t('dspBill.exportModal.templateType')}: {latestRecord.templateType}</li>
                                </ul>
                                <p style={{marginTop: 12, color: '#faad14'}}>
                                    <strong>{t('dspBill.exportModal.tipPrefix')}</strong>{t('dspBill.exportModal.reExportNote')}
                                </p>
                                <p>{t('dspBill.exportModal.continueExport')}</p>
                            </div>
                        ),
                        okText: t('dspBill.exportModal.okText'),
                        cancelText: t('dspBill.exportModal.cancelText'),
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false),
                        width: 520
                    });
                });
                if (!confirmResult) return;
            } else {
                const confirmResult = await new Promise<boolean>((resolve) => {
                    Modal.confirm({
                        title: t('dspBill.exportModal.confirmTitle'),
                        content: (
                            <div>
                                <p>{t('dspBill.exportModal.newExportTip')}</p>
                                <ul style={{marginTop: 8, marginLeft: 20}}>
                                    <li>{t('dspBill.fields.billId')}: {selectedBill.id}</li>
                                    <li>{t('dspBill.fields.dspCustomer')}: {selectedBill.clientName}</li>
                                    <li>{t('dspBill.fields.billType')}: {getBillTypeLabel(selectedBill.billType)}</li>
                                    <li>{t('dspBill.fields.billAmount')}: {selectedBill.billAmount}</li>
                                </ul>
                                <p style={{marginTop: 12}}>{t('dspBill.exportModal.confirmExport')}</p>
                            </div>
                        ),
                        okText: t('dspBill.exportModal.okText'),
                        cancelText: t('dspBill.exportModal.cancelText'),
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false)
                    });
                });
                if (!confirmResult) return;
            }

            const result = await exportBill({
                variables: {
                    exportRequest: {
                        billId: selectedBill.id,
                        template,
                        format: 'EXCEL',
                        language
                    }
                }
            });

            if (result.data?.exportBill?.success) {
                const taskIds = result.data.exportBill.taskIds || [];
                message.success(t('dspBill.exportModal.taskCreated', {count: taskIds.length}));
                setExportModalVisible(false);
                setExportTaskId(taskIds.length > 0 ? taskIds.join(',') : '');
                setProgressModalVisible(true);
            } else {
                message.error(result.data?.exportBill?.errorMessage || t('dspBill.exportModal.exportFailed'));
            }
        } catch (error) {
            message.destroy('check');
            message.error(`${t('dspBill.exportModal.exportFailed')}: ${(error as Error).message}`);
        }
    };

    const handleExportCompleted = (task: any) => {
        if (task.status === 'SUCCESS') {
            message.success(t('dspBill.exportModal.exportSuccess'));
        } else if (task.status === 'FAILED') {
            message.error(`${t('dspBill.exportModal.exportFailed')}: ${task.errorMessage || t('dspBill.exportModal.unknownError')}`);
        }
        refetch();
    };

    const handleConfirmBill = (bill: SettlementBillType) => {
        Modal.confirm({
            title: t('dspBill.confirmModal.title'),
            content: t('dspBill.confirmModal.content'),
            okText: t('dspBill.confirmModal.okText'),
            cancelText: t('dspBill.confirmModal.cancelText'),
            onOk: async () => {
                try {
                    const result = await confirmBill({variables: {billId: bill.id}});
                    if (result.data?.confirmBill?.success) {
                        message.success(t('dspBill.confirmModal.confirmSuccess'));
                        await refetch();
                    } else {
                        message.error(result.data?.confirmBill?.errorMessage || t('dspBill.confirmModal.confirmFailed'));
                    }
                } catch (err) {
                    message.error(`${t('dspBill.confirmModal.confirmFailed')}: ${(err as Error).message}`);
                }
            }
        });
    };

    const handleRejectBillExternal = (bill: SettlementBillType) => {
        let remark = '';
        Modal.confirm({
            title: t('dspBill.rejectModal.title'),
            content: (
                <div className="pb-6">
                    <p className="mb-2">
                        {t('dspBill.rejectModal.instruction')}
                    </p>
                    <Input.TextArea
                        rows={4}
                        maxLength={500}
                        showCount
                        placeholder={t('dspBill.rejectModal.placeholder') as string}
                        onChange={(e) => {
                            remark = e.target.value;
                        }}
                    />
                </div>
            ),
            okText: t('dspBill.rejectModal.okText'),
            cancelText: t('dspBill.rejectModal.cancelText'),
            okType: 'danger',
            onOk: async () => {
                if (!remark.trim()) {
                    message.warning(t('dspBill.rejectModal.reasonRequired'));
                    return Promise.reject(new Error('remark required'));
                }
                try {
                    const result = await rejectBillExternal({variables: {billId: bill.id, remark: remark.trim()}});
                    if (result.data?.rejectBillExternal?.success) {
                        message.success(t('dspBill.rejectModal.rejectSuccess'));
                        await refetch();
                    } else {
                        message.error(result.data?.rejectBillExternal?.errorMessage || t('dspBill.rejectModal.rejectFailed'));
                        return Promise.reject(new Error('reject failed'));
                    }
                } catch (err) {
                    message.error(`${t('dspBill.rejectModal.rejectFailed')}: ${(err as Error).message}`);
                    return Promise.reject(err as Error);
                }
            }
        });
    };

    const formatDateOnly = (timeString: string | undefined | null, timeZone: string) => {
        if (!timeString) return '-';
        try {
            return formatDateWithTimezone(timeString, timeZone, i18n.language);
        } catch (error) {
            console.error('Date formatting error:', error);
            return timeString;
        }
    };

    const columns = [
        {
            title: t('dspBill.fields.billId'),
            dataIndex: 'id',
            key: 'id',
            width: 120,
        },
        {
            title: t('dspBill.fields.billType'),
            dataIndex: 'billType',
            key: 'billType',
            width: 120,
            render: (type: string) => renderBillTypeTag(type),
        },
        {
            title: t('dspBill.fields.dspCustomer'),
            dataIndex: 'clientName',
            key: 'clientName',
            width: 150,
        },
        {
            title: t('dspBill.fields.clientId'),
            dataIndex: 'channelId',
            key: 'channelId',
            width: 180,
        },
        {
            title: t('dspBill.fields.settlementClientId'),
            dataIndex: 'clientId',
            key: 'clientId',
            width: 180,
        },
        {
            title: t('dspBill.fields.billAmount'),
            dataIndex: 'billAmount',
            key: 'billAmount',
            width: 150,
            render: (amount: string) => <span style={{whiteSpace: 'nowrap'}}>{amount}</span>,
        },
        {
            title: 'Invoice金额',
            dataIndex: 'invoiceAmount',
            key: 'invoiceAmount',
            width: 170,
            render: (_: unknown, record: SettlementBillType) => {
                if (record.invoiceAmount == null) return <span className="text-gray-400">-</span>;
                const text = formatCentsAmount(record.invoiceAmount, record.invoiceCurrency);
                if (record.invoiceAmountMismatch) {
                    return (
                        <span style={{whiteSpace: 'nowrap'}}>
                            <span className="text-orange-600 mr-1">{text}</span>
                            <Tooltip title="Invoice 金额与账单结算金额不一致">
                                <WarningOutlined className="text-orange-500" />
                            </Tooltip>
                        </span>
                    );
                }
                return <span style={{whiteSpace: 'nowrap'}}>{text}</span>;
            },
        },
        {
            title: t('dspBill.list.billStatus'),
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: string, record: SettlementBillType) => {
                if (record.isCanceled) {
                    return <Tag color="red">{t('enum.billStatus.CANCELED')}</Tag>;
                }
                return (
                    <Tag color={getBillStatusColor(status)}>
                        {status ? t(`enum.billStatus.${status}`, {defaultValue: status}) : ''}
                    </Tag>
                );
            },
        },
        {
            title: t('dspInvoice.fields.submitted'),
            dataIndex: 'invoiceSubmitted',
            key: 'invoiceSubmitted',
            width: 120,
            render: (invoiceSubmitted: boolean) => (
                invoiceSubmitted
                    ? <Tag color="green">{t('dspInvoice.submitted.yes')}</Tag>
                    : <Tag>{t('dspInvoice.submitted.no')}</Tag>
            ),
        },
        {
            title: t('dspBill.fields.billPeriod'),
            dataIndex: 'periodStart',
            key: 'periodRange',
            width: 280,
            render: (start: string, record: SettlementBillType) => {
                const timeZone = record.timeZone || 'UTC';
                const formattedStart = formatDateOnly(start, timeZone);
                const formattedEnd = formatDateOnly(record.periodEnd, timeZone);
                return (
                    <span style={{whiteSpace: 'nowrap'}}>
                        {formattedStart} - {formattedEnd}
                    </span>
                );
            },
        },
        {
            title: t('dspBill.fields.timezone'),
            dataIndex: 'timeZone',
            key: 'timeZone',
            width: 150,
        },
        {
            title: t('dspBill.fields.action'),
            key: 'action',
            width: 340,
            render: (record: SettlementBillType) => {
                const showConfirmBillButton = record.status === 'INTERNAL_CONFIRMED' && !record.isCanceled;
                const canRejectByCurrentRole = record.status === 'INTERNAL_CONFIRMED' && !record.isCanceled;
                return (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined/>}
                        onClick={() => handleViewDetail(record)}
                    >
                        {t('dspBill.list.viewDetail')}
                    </Button>
                    <Button
                        type="link"
                        icon={<ExportOutlined/>}
                        onClick={() => handleExportBill(record)}
                        disabled={!canExportBill(record)}
                        title={getExportButtonTitle(record)}
                    >
                        {t('dspBill.list.exportBill')}
                    </Button>
                    {showConfirmBillButton && (
                        <Button
                            type="link"
                            onClick={() => handleConfirmBill(record)}
                            loading={confirmBillLoading}
                            disabled={confirmBillLoading}
                        >
                            {t('dspBill.list.confirmBill')}
                        </Button>
                    )}
                    {canRejectByCurrentRole && (
                        <Button
                            type="link"
                            danger
                            onClick={() => handleRejectBillExternal(record)}
                        >
                            {t('dspBill.list.rejectBillShort')}
                        </Button>
                    )}
                </Space>
                );
            },
        },
    ];

    return (
        <div className="p-6">
            <Card title={t('dspBill.list.billQuery')} className="mb-4">
                <div className="space-y-3">
                    <div className="flex items-center">
                        <label className="text-sm font-medium text-gray-700 w-32">{t('dspBill.list.billType')}</label>
                        <Select
                            placeholder={t('dspBill.list.billTypePlaceholder')}
                            allowClear
                            value={tempFilters.billType}
                            onChange={handleBillTypeChange}
                            className="w-64"
                        >
                            <Option value="DSP_DELIVERY_FEE_BILL">{t('enum.billType.DSP_DELIVERY_FEE_BILL')}</Option>
                        </Select>
                    </div>

                    <div className="flex items-center">
                        <label className="text-sm font-medium text-gray-700 w-32">{t('dspBill.list.billStatus')}</label>
                        <Select
                            placeholder={t('dspBill.list.billStatusPlaceholder')}
                            allowClear
                            value={tempFilters.billStatus}
                            onChange={handleBillStatusChange}
                            className="w-64"
                            loading={billStatusOptionsLoading}
                        >
                            {(billStatusOptionsData?.getBillStatusFilterOptions || []).map(
                                (option: {value: FrontendBillStatusEnum; label: string}) => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                )
                            )}
                        </Select>
                    </div>

                    <div className="flex items-center">
                        <label className="text-sm font-medium text-gray-700 w-32">{t('dspInvoice.fields.submitted')}</label>
                        <Select
                            placeholder={t('dspBill.list.invoiceSubmittedPlaceholder')}
                            allowClear
                            value={tempFilters.invoiceSubmitted}
                            onChange={handleInvoiceSubmittedChange}
                            className="w-64"
                        >
                            <Option value={false}>{t('dspInvoice.submitted.no')}</Option>
                            <Option value={true}>{t('dspInvoice.submitted.yes')}</Option>
                        </Select>
                    </div>

                    <div className="flex items-center">
                        <label className="text-sm font-medium text-gray-700 w-32">{t('dspBill.list.billTimeRange')}</label>
                        <BillTimeRangePicker
                            value={tempFilters.billTimeRange}
                            onChange={handleTimeRangeChange}
                            defaultTimeZoneId="America/Los_Angeles"
                        />
                    </div>
                </div>

                <div className="flex justify-start items-center gap-3 pt-4 mt-2 border-t border-gray-200">
                    <Button
                        type="primary"
                        icon={<SearchOutlined/>}
                        onClick={handleSearch}
                        size="large"
                    >
                        {t('common.search')}
                    </Button>
                </div>
            </Card>

            <Card title={t('dspBill.list.billList')}>
                <Table
                    columns={columns}
                    dataSource={data?.getDeliveryDspBillListing?.content || []}
                    loading={loading}
                    rowKey="id"
                    pagination={false}
                    scroll={{x: 1200}}
                    className="mb-4"
                />
                {data?.getDeliveryDspBillListing?.pageInfo && (
                    <TablePaginationBar
                        total={data.getDeliveryDspBillListing.pageInfo.totalElements}
                        current={data.getDeliveryDspBillListing.pageInfo.page}
                        pageSize={data.getDeliveryDspBillListing.pageInfo.size}
                        onChange={handlePageChange}
                    />
                )}
            </Card>

            <BillExportModal
                visible={exportModalVisible}
                bill={selectedBill}
                onCancel={() => setExportModalVisible(false)}
                onExport={handleExport}
            />

            <BillExportProgressModal
                taskId={exportTaskId}
                visible={progressModalVisible}
                onClose={() => {
                    setProgressModalVisible(false);
                    setExportTaskId(null);
                }}
                onCompleted={handleExportCompleted}
            />
        </div>
    );
};

export default DeliveryDspBillListPage;
